import React, { useEffect, useId, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  buildArchiveAdminProspectLetterTemplateRequest,
  archiveAdminProspectLetterTemplate,
  createAdminProspectLetterTemplateWriteAttempt,
} from '../../services/admin/prospectLetterTemplatesService.js';
import {
  adminProspectLetterTemplateErrorKeyFromError,
  classifyAdminCallableError,
} from './adminProspectLetterTemplateDisplay.js';

export default function AdminArchiveLetterTemplateDialog({ open, template, onClose, onSuccess }) {
  const titleId = useId();
  const descId = useId();
  const { t } = useLanguage();
  const { impersonation } = useAuth();
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [retryable, setRetryable] = useState(false);
  const archiveAttemptRef = useRef(createAdminProspectLetterTemplateWriteAttempt());
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    archiveAttemptRef.current.reset();
    setSaving(false);
    setErrorKey(null);
    setRetryable(false);
  }, [open, template?.templateId]);

  if (!open || !template?.templateId) return null;

  const impersonating = impersonation?.active === true;

  const handleSubmit = async () => {
    if (submitLockRef.current || impersonating) return;
    submitLockRef.current = true;
    setSaving(true);
    setErrorKey(null);
    setRetryable(false);
    try {
      const payload = archiveAttemptRef.current.resolveAttemptPayload({
        fingerprint: template.templateId,
        build: (operationId) =>
          buildArchiveAdminProspectLetterTemplateRequest({
            operationId,
            templateId: template.templateId,
          }),
      });
      await archiveAdminProspectLetterTemplate(payload);
      archiveAttemptRef.current.discard();
      if (typeof onSuccess === 'function') onSuccess();
      onClose();
    } catch (err) {
      setErrorKey(adminProspectLetterTemplateErrorKeyFromError(err));
      setRetryable(classifyAdminCallableError(err) === 'unavailable');
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">
          {t('admin.prospection.letterTemplate.archive.title')}
        </h2>
        <p id={descId} className="mt-2 text-sm text-slate-600">
          {t('admin.prospection.letterTemplate.archive.body')}
        </p>
        {impersonating ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
            {t('admin.prospection.letterTemplate.impersonationBlocked')}
          </p>
        ) : null}
        {errorKey ? (
          <p className="mt-3 text-sm text-rose-800" role="alert">
            {t(errorKey)}
            {retryable ? (
              <button type="button" className="ml-2 underline" onClick={handleSubmit}>
                {t('admin.prospection.letterTemplate.action.retry')}
              </button>
            ) : null}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
          >
            {t('admin.prospection.letterTemplate.archive.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || impersonating}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving
              ? t('admin.prospection.letterTemplate.archive.saving')
              : t('admin.prospection.letterTemplate.archive.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
